import React, { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LuX } from "react-icons/lu";
import { useSession } from "../context/SessionContext";
import { useAccess } from "../hooks/useAccess";
import { useBillingPortal } from "../hooks/useBillingPortal";
import { useFaturaEmAberto } from "../hooks/useFaturaEmAberto";
import { useSessionStorage } from "../hooks/useSessionStorage";
import { WHATSAPP_PLANO_CLINICA } from "../constants/billing";
import { billingNotice, isDismissable, kitUsageParts, NoticeAction, NoticeTone } from "../features/billing/billingNotice";
import { cn } from "../components/ui/cn";
import { t } from "../i18n";
import { PATHS } from "./paths";

const TONE: Record<NoticeTone, string> = {
  warning: "bg-warning-soft text-warning border-warning/30",
  danger: "bg-danger-soft text-danger-hover border-danger/30",
};

/**
 * Publica a altura da faixa em `--billing-banner-h` (no <html>) para as
 * páginas de altura fixa descontarem — a faixa quebra em várias linhas no
 * celular, então só a medida real serve.
 */
function useBannerHeightVar() {
  const observer = useRef<ResizeObserver | null>(null);
  const setHeight = (px: number) =>
    document.documentElement.style.setProperty("--billing-banner-h", `${px}px`);

  // Ao desmontar, o React chama o ref com null e a altura volta a 0 ali. Um
  // cleanup de useEffect aqui zerava a medida no StrictMode (roda depois do ref).
  return useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) {
      setHeight(0);
      return;
    }
    setHeight(el.offsetHeight);
    if (typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.current.observe(el);
  }, []);
}

/** Faixa fixa de aviso de cobrança e uso de pacotes. Montada uma vez, no ProtectedRoute. */
export const BillingNotice: React.FC = () => {
  const bannerRef = useBannerHeightVar();
  const navigate = useNavigate();
  const { billing, papel } = useSession();
  const { canViewBilling, canManagePlan, isClinicPlan } = useAccess();
  const { goToPortal, loading: portalLoading } = useBillingPortal();
  const { abrirFatura, loading: invoiceLoading } = useFaturaEmAberto();
  const [dismissedAviso, setDismissedAviso] = useSessionStorage<string>("billingAvisoDismissed", "");

  if (!billing) return null;

  const notice = billingNotice(billing, {
    isClinicOwner: billing.modo === "clinica" && papel === "dono",
    isClinicPlan,
    canViewBilling,
    canManagePlan,
  });
  const showNotice = notice && dismissedAviso !== billing.aviso;
  const usage = kitUsageParts(billing);
  if (!showNotice && usage.length === 0) return null;

  const loading = portalLoading || invoiceLoading;
  const actions: Record<NoticeAction, { label: string; onClick: () => void }> = {
    pay: { label: invoiceLoading ? t("billing.notice.cta.opening") : t("billing.notice.cta.pay"), onClick: abrirFatura },
    updateCard: { label: portalLoading ? t("billing.notice.cta.opening") : t("billing.notice.cta.updateCard"), onClick: goToPortal },
    reactivate: { label: portalLoading ? t("billing.notice.cta.opening") : t("billing.notice.cta.reactivate"), onClick: goToPortal },
    subscribe: { label: t("billing.notice.cta.subscribe"), onClick: () => navigate(PATHS.plans) },
    talkToUs: { label: t("billing.notice.cta.talkToUs"), onClick: () => window.open(WHATSAPP_PLANO_CLINICA, "_blank", "noopener,noreferrer") },
  };
  const action = notice?.action && actions[notice.action];

  return (
    <div ref={bannerRef} className="sticky top-0 z-30 w-full">
      {showNotice && (
        <div
          role="status"
          className={cn(
            "flex w-full items-center justify-between gap-2 border-b px-3 py-1.5 text-badge font-medium sm:flex-wrap sm:justify-center sm:gap-3 sm:px-4 sm:py-2.5 sm:text-center sm:text-caption",
            TONE[notice.tone]
          )}
        >
          <span className="min-w-0 flex-1 sm:flex-none">{notice.text}</span>
          <div className="flex shrink-0 items-center gap-2">
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                disabled={loading}
                className="min-h-control-touch whitespace-nowrap rounded-full bg-fg/10 px-3 text-badge font-semibold hover:bg-fg/15 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0 sm:py-1"
              >
                {action.label}
              </button>
            )}
            {isDismissable(billing.aviso) && (
              <button
                type="button"
                aria-label={t("billing.notice.dismiss")}
                onClick={() => setDismissedAviso(billing.aviso)}
                className="grid min-h-control-touch min-w-control-touch place-items-center opacity-70 hover:opacity-100 sm:min-h-0 sm:min-w-0"
              >
                <LuX aria-hidden />
              </button>
            )}
          </div>
        </div>
      )}
      {usage.length > 0 && (
        <div className="w-full border-b border-line bg-surface-muted px-3 py-1 text-center text-badge text-fg-muted sm:px-4 sm:py-1.5">
          {usage.join(" · ")}
        </div>
      )}
    </div>
  );
};
